import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { StatusCode } from "api/protocols";
import { PostDuplicatePricingSheet } from "api/rpc/2024-04/facilityAdmin/teesheet/pricingSheet";
import Callout from "components/callout/Callout";
import { Select } from "components/select/index";
import Sheet from "components/sheet/Sheet";
import { useAppDispatch } from "hooks/redux";
import { showError, showSuccess } from "redux/actions/ui";
import { TTeesheetPricingSheet } from "redux/reducers/models/teesheet";

type CourseOption = {
  id: number;
  full_name?: string;
};

interface IDuplicatePricingSheetModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  pricingSheetIds: number[];
  sourceDayLabel: string;
  pricingSheets: TTeesheetPricingSheet[] | undefined;
  courses: CourseOption[];
  defaultCourseId: number;
}

const COPY_TO_DAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
  "Holiday",
] as const;

const DAY_ABBREVIATIONS: Record<string, string> = {
  Monday: "Mon",
  Tuesday: "Tue",
  Wednesday: "Wed",
  Thursday: "Thu",
  Friday: "Fri",
  Saturday: "Sat",
  Sunday: "Sun",
  Holiday: "Holiday",
};

function formatConflictDayLabels(days: string[]): string {
  const labels = days.map(day => DAY_ABBREVIATIONS[day] ?? day);

  if (labels.length === 0) {
    return "";
  }

  if (labels.length === 1) {
    return labels[0];
  }

  if (labels.length === 2) {
    return `${labels[0]} & ${labels[1]}`;
  }

  return `${labels.slice(0, -1).join(", ")} & ${labels[labels.length - 1]}`;
}

export default function DuplicatePricingSheetModal(props: IDuplicatePricingSheetModalProps) {
  const {
    open,
    onClose,
    onSuccess,
    pricingSheetIds,
    sourceDayLabel,
    pricingSheets,
    courses,
    defaultCourseId,
  } = props;
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { Option } = Select;

  const [selectedDates, setSelectedDates] = useState<string[]>([]);
  const [selectedCourseId, setSelectedCourseId] = useState<number>(defaultCourseId);
  const [warningDismissed, setWarningDismissed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const selectedSheets = useMemo(
    () => pricingSheets?.filter(sheet => pricingSheetIds.includes(sheet.id)) ?? [],
    [pricingSheets, pricingSheetIds],
  );

  useEffect(() => {
    if (open) {
      const preferredCourseId = selectedSheets[0]?.course_id ?? defaultCourseId;
      setSelectedCourseId(preferredCourseId);
      return;
    }

    setSelectedDates([]);
    setSelectedCourseId(defaultCourseId);
    setWarningDismissed(false);
    setIsSubmitting(false);
    // Clear on close so the next open doesn't flash the previous selection
  }, [open]);

  const conflictingDays = useMemo(() => {
    if (!pricingSheets || selectedSheets.length === 0 || selectedDates.length === 0) {
      return [];
    }

    const isMultiCourse = courses.length > 1;

    return selectedDates.filter(dayLabel =>
      selectedSheets.some(selectedSheet =>
        pricingSheets.some(existingSheet => {
          if (existingSheet.day_of_week !== dayLabel) {
            return false;
          }

          if (existingSheet.application !== selectedSheet.application) {
            return false;
          }

          if (isMultiCourse && existingSheet.course_id !== selectedCourseId) {
            return false;
          }

          return true;
        }),
      ),
    );
  }, [pricingSheets, selectedSheets, selectedDates, courses.length, selectedCourseId]);

  function handleDayChipClick(day: string) {
    if (day === sourceDayLabel) {
      return;
    }

    setSelectedDates(prev => (prev.includes(day) ? prev.filter(selected => selected !== day) : [...prev, day]));
  }

  async function handleDuplicate() {
    if (pricingSheetIds.length === 0 || selectedDates.length === 0) {
      return;
    }

    setIsSubmitting(true);

    const duplicateRes = await Promise.all(
      selectedDates.map(day =>
        PostDuplicatePricingSheet(
          {
            pricing_sheet_ids: pricingSheetIds,
            day_of_week: day,
            course_id: selectedCourseId,
          },
          true,
        ),
      ),
    );

    setIsSubmitting(false);

    if (duplicateRes?.some(res => res?.status !== StatusCode.OK)) {
      dispatch(showError(t("Error duplicating check in configs")));
      return;
    }

    dispatch(showSuccess(t("Successfully duplicated pricing sheets")));
    onSuccess();
  }

  const dayCount = selectedDates.length;
  const okText =
    dayCount === 1 ? t("Duplicate to 1 day") : t("Duplicate to {{count}} days", { count: dayCount });

  return (
    <Sheet
      overflow
      open={open}
      size="small"
      closable
      title={t("Duplicate pricing sheet")}
      supportText={t("Copies this sheet's products and overrides to the days you pick.")}
      onCancel={onClose}
      onOk={handleDuplicate}
      cancelText={t("Cancel")}
      okText={okText}
      okLoading={isSubmitting}
      okDisabled={pricingSheetIds.length === 0 || selectedDates.length === 0 || isSubmitting}
    >
      {courses.length > 1 && (
        <Select
          key={`duplicate-course-${selectedCourseId}`}
          onChange={(courseId: number) => setSelectedCourseId(courseId)}
          label={t("Course")}
          defaultValue={selectedCourseId}
          className="mb-4"
        >
          {courses.map(course => (
            <Option key={course.id} value={course.id}>
              {course.full_name}
            </Option>
          ))}
        </Select>
      )}

      <div className="pricing-sheets-duplicate-copy-to">
        <p className="pricing-sheets-duplicate-copy-to-label">{t("Copy to:")}</p>
        <div className="pricing-sheets-duplicate-day-chips">
          {COPY_TO_DAYS.map(day => {
            const isSourceDay = day === sourceDayLabel;
            const isSelected = selectedDates.includes(day);

            return (
              <button
                key={day}
                type="button"
                className={`pricing-sheets-duplicate-day-chip${isSelected ? " is-selected" : ""}${
                  isSourceDay ? " is-disabled" : ""
                }`}
                disabled={isSourceDay}
                onClick={() => handleDayChipClick(day)}
              >
                {t(day)}
              </button>
            );
          })}
        </div>
      </div>

      {!warningDismissed && conflictingDays.length > 0 && (
        <div className="pricing-sheets-duplicate-warning">
          <Callout
            type="alerts"
            title=""
            content={
              <span>
                <strong>{formatConflictDayLabels(conflictingDays)}</strong> already have pricing sheets set.
                Duplicating will <strong>overwrite</strong> them.
              </span>
            }
            onClose={() => setWarningDismissed(true)}
          />
        </div>
      )}

      {pricingSheetIds.length === 0 && (
        <Callout
          type="error"
          title={t("No Pricing Sheets Selected")}
          content={t("Select the check boxes of the pricing sheets you wish to duplicate and try again")}
        />
      )}
    </Sheet>
  );
}
